import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bookmark, BookmarkX, ExternalLink, Mail, ShieldCheck } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

function ScoreBadge({ value }) {
    const v = Math.max(0, Math.min(100, value || 0));
    const tone =
        v >= 75
            ? "text-[var(--color-success)] border-[var(--color-success)]/40 bg-[var(--color-success)]/10"
            : v >= 50
              ? "text-[var(--color-secondary)] border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10"
              : "text-text-secondary border-[var(--color-border)] bg-[var(--color-panel-ai)]";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs tabular-nums ${tone}`}
        >
            {v}
        </span>
    );
}

export default function SavedLeads() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await api.leadsSaved(200, 0);
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.message || "Unable to load saved leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const unsave = async (lead) => {
        try {
            await api.leadsPatch(lead.id, { saved: false });
            setItems((cur) => cur.filter((l) => l.id !== lead.id));
        } catch (e) {
            toast.error(e.message || "Unable to unsave");
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((c) => !c)}
            />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main
                    data-testid="saved-leads-main"
                    className="flex-1 px-4 md:px-6 py-6 max-w-6xl w-full"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                                Saved leads
                            </p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                La tua lista{" "}
                                <span className="gradient-text">bookmark</span>
                            </h1>
                            <p className="mt-1 text-sm text-text-secondary max-w-2xl">
                                Ripescabili in Fase 2 (Outreach) per lanciare
                                sequenze mirate.
                            </p>
                        </div>
                        <CreditUsageWidget />
                    </div>

                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        {loading ? (
                            <div className="p-6 text-sm text-text-secondary">
                                Loading…
                            </div>
                        ) : items.length === 0 ? (
                            <div
                                data-testid="saved-leads-empty"
                                className="p-12 text-center"
                            >
                                <div className="mx-auto h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Bookmark className="h-5 w-5 text-[var(--color-secondary)]" />
                                </div>
                                <h3 className="mt-4 text-base font-semibold">
                                    Nessun lead salvato
                                </h3>
                                <p className="mt-2 text-sm text-text-secondary max-w-md mx-auto">
                                    Su /leads clicca l'icona bookmark accanto a
                                    ogni lead per aggiungerlo qui.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[11px] uppercase tracking-widest text-text-secondary/80 border-b border-[var(--color-border)]">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-normal">Name</th>
                                            <th className="text-left px-4 py-2 font-normal">Company</th>
                                            <th className="text-left px-4 py-2 font-normal">Role</th>
                                            <th className="text-left px-4 py-2 font-normal">Email</th>
                                            <th className="text-left px-4 py-2 font-normal">LinkedIn</th>
                                            <th className="text-center px-4 py-2 font-normal">Score</th>
                                            <th className="text-right px-4 py-2 font-normal">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((l, i) => (
                                            <motion.tr
                                                key={l.id}
                                                initial={{ opacity: 0, y: 2 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: i * 0.01,
                                                }}
                                                data-testid={`saved-lead-row-${i}`}
                                                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                            >
                                                <td className="px-4 py-3 font-medium">
                                                    {l.full_name}
                                                </td>
                                                <td className="px-4 py-3 text-text-secondary">
                                                    {l.company}
                                                </td>
                                                <td className="px-4 py-3 text-text-secondary">
                                                    {l.job_title}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {l.email ? (
                                                        <span className="inline-flex items-center gap-1 text-xs">
                                                            <Mail className="h-3 w-3 text-text-secondary" />
                                                            {l.email}
                                                            {l.email_verified && (
                                                                <ShieldCheck className="h-3 w-3 text-[var(--color-success)]" />
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="text-text-secondary/60 text-xs">
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {l.linkedin_url ? (
                                                        <a
                                                            href={l.linkedin_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-1 text-xs text-[var(--color-secondary)] hover:underline"
                                                        >
                                                            Profile
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    ) : (
                                                        <span className="text-text-secondary/60 text-xs">
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <ScoreBadge value={l.ai_score} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-end">
                                                        <button
                                                            data-testid={`saved-unsave-${i}`}
                                                            onClick={() => unsave(l)}
                                                            title="Remove from saved"
                                                            className="rounded-md p-1.5 text-text-secondary hover:text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10 transition-colors"
                                                        >
                                                            <BookmarkX className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
